import axiosInstance from "./axiosInstance";

export interface FilterDto {
    page?: number;
    limit?: number;
    status?: PayoutFundingStatusEnum;
}

export interface SubscribePayoutServiceDto {
    customerId: string;
    partnerServiceId: string;
    customRate?: number;
    fixedFee?: number;
    minAmount?: number;
    maxAmount?: number;
    notes?: string;
    isActive?: boolean;
}

export interface UpdatePayoutServiceDto {
    customRate?: number;
    fixedFee?: number;
    minAmount?: number;
    maxAmount?: number;
    isActive?: boolean;
    notes?: string;
}

export enum PayoutTransactionStatusEnum {
    PENDING    = 'PENDING',
    PROCESSING = 'PROCESSING',
    SUCCESS    = 'SUCCESS',
    FAILED     = 'FAILED',
    CANCELLED  = 'CANCELLED',
}

export enum PayoutFundingStatusEnum {
    PENDING    = 'PENDING',
    APPROVED   = 'APPROVED',
    PROCESSING = 'PROCESSING',
    COMPLETED  = 'COMPLETED',
    REJECTED   = 'REJECTED',
    CANCELLED  = 'CANCELLED',
}

export interface FilterPayoutTransactionsDto {
    status?: PayoutTransactionStatusEnum;
    currency?: string;
    startDate?: string;
    endDate?: string;
    recipientMsisdn?: string;
    partnerId?: string;
    page?: number;
    limit?: number;
}

export enum PayoutFundingPaymentMethodEnum {
    MOBILE_MONEY  = 'MOBILE_MONEY',   // Virement mobile money externe (OM, MTN, Wave…)
    BANK_TRANSFER = 'BANK_TRANSFER',  // Virement bancaire externe
    PAYIN_ACCOUNT = 'PAYIN_ACCOUNT',  // Débit interne depuis le solde payin du customer
    OTHER         = 'OTHER',          // Autre méthode non prévue — préciser dans externalReference ou notes
}

export interface PayoutTransactionDto {
    amount: number;
    paymentMethod:PayoutFundingPaymentMethodEnum;
    externalReference?: string;
    notes?: string;
}

export interface ExecutePayoutDto {
    amount: number;
    recipientMsisdn: string;
    recipientName?: string;
    payoutSubscriptionId: string;
    callbackUrl?: string;
}

const getAllAccounts = async (params: FilterDto) => {
    const response = await axiosInstance.get("payout/admin/accounts",{
        params: {
            page: params.page || 1,
            limit: params.limit || 10,
        }
    });
    return response.data;
}

const activateAccount = async (customerId: string) => {
    const response = await axiosInstance.post(`payout/admin/accounts/${customerId}/activate`);
    return response.data;
};

const deactivateAccount = async (customerId: string) => {
    const response = await axiosInstance.post(`payout/admin/accounts/${customerId}/deactivate`);
    return response.data;
};

const subscribeService = async (data : SubscribePayoutServiceDto) => {
    const response = await axiosInstance.post("payout/admin/service/subscribe", data);
    return response.data;
}

const updateService = async (subscriptionId: string, data: UpdatePayoutServiceDto) => {
    const response = await axiosInstance.put(`payout/admin/service/${subscriptionId}`, data);
    return response.data;
}

const deactivateService = async (subscriptionId: string) => {
    const response = await axiosInstance.post(`payout/admin/service/${subscriptionId}/deactivate`);
    return response.data;
};

const getOneService = async (subscriptionId: string) => {
    const response = await axiosInstance.get(`payout/admin/service/${subscriptionId}`);
    return response.data;
};

const getServicesForCustomer = async (customerId: string) => {
    const response = await axiosInstance.get(`payout/admin/service/customer/${customerId}`);
    return response.data;
};

const getAllTransactions = async (data: FilterPayoutTransactionsDto) => {
    const response = await axiosInstance.get(`payout/admin/transactions`, {
        params: {
            status: data.status,
            startDate: data.startDate,
            endDate: data.endDate,
            recipientMsisdn: data.recipientMsisdn,
            partnerId: data.partnerId,
            page: data.page ?? 1,
            limit: data.limit ?? 10,
        }
    });
    return response.data;
};

const activateAccountCustomer = async () => {
    const response = await axiosInstance.post(`payout/customer/account/activate`);
    return response.data;
}

const getAccount = async () => {
    const response = await axiosInstance.get(`payout/customer/account`);
    return response.data;
}

const initiatePayout = async (data: ExecutePayoutDto) => {
    const response = await axiosInstance.post(`payout/customer/execute`, data);
    return response.data;
}

const fundAccount = async (data: PayoutTransactionDto) => {
    const response = await axiosInstance.post(`payout/customer/account/fund/request`, data);
    return response.data;
}

const getFundings = async (params: FilterDto) => {
    const response = await axiosInstance.get(`payout/customer/account/fundings`, {
        params: {
            page: params.page || 1,
            limit: params.limit || 10,
        }
    });
    return response.data;
}

const getAllFundingsAdmin = async (params: FilterDto) => {
    const response = await axiosInstance.get(`payout/admin/fundings`, {
        params: {
            page: params.page || 1,
            limit: params.limit || 10,
            status: params.status,
        }
    });
    return response.data;
}

const approveFundingAdmin = async (fundingId: string, adminNotes?: string) => {
    const response = await axiosInstance.post(`payout/admin/fundings/${fundingId}/approve`, {
        adminNotes,
    });
    return response.data;
}

const rejectFundingAdmin = async (fundingId: string, adminNotes?: string) => {
    const response = await axiosInstance.post(`payout/admin/fundings/${fundingId}/reject`, {
        adminNotes,
    });
    return response.data;
}

const processFundingAdmin = async (fundingId: string, adminNotes?: string) => {
    const response = await axiosInstance.post(`payout/admin/fundings/${fundingId}/process`, {
        adminNotes,
    });
    return response.data;
}

const completeFundingAdmin = async (fundingId: string, adminNotes?: string, externalReference?: string) => {
    const response = await axiosInstance.post(`payout/admin/fundings/${fundingId}/complete`, {
        adminNotes,
        externalReference,
    });
    return response.data;
}

const cancelFundingAdmin = async (fundingId: string, adminNotes?: string) => {
    const response = await axiosInstance.post(`payout/admin/fundings/${fundingId}/cancel`, {
        adminNotes,
    });
    return response.data;
}

const getTransactionCustomer = async (params: FilterPayoutTransactionsDto) => {
    const response = await axiosInstance.get(`payout/customer/transactions`, {
        params: {
            status: params.status || undefined,
            currency: params.currency || undefined,
            startDate: params.startDate,
            endDate: params.endDate,
            recipientMsisdn: params.recipientMsisdn,
            partnerId: params.partnerId,
            page: params.page || 1,
            limit: params.limit || 10,
        }
    });
    return response.data;
}

const getOneTransaction = async (transactionID: string) => {
    const response = await axiosInstance.get(`payout/customer/transactions/${transactionID}`);
    return response.data;
}

const getCustomerStats = async (filters: FilterPayoutTransactionsDto) => {
    const response = await axiosInstance.get('payout/customer/stats', {
        params: {
            status: filters.status || undefined,
            currency: filters.currency || undefined,
            startDate: filters.startDate,
            endDate: filters.endDate,
            recipientMsisdn: filters.recipientMsisdn,
        },
    });
    return response.data;
}

const getCustomerStatsByCurrency = async (filters: FilterPayoutTransactionsDto) => {
    const response = await axiosInstance.get('payout/customer/stats-by-currency', {
        params: {
            status: filters.status || undefined,
            startDate: filters.startDate,
            endDate: filters.endDate,
            recipientMsisdn: filters.recipientMsisdn,
        },
    });
    return response.data;
}

const getMyServices = async () => {
    const response = await axiosInstance.get(`payout/customer/services`);
    return response.data;
}

const getBalanceHistory = async (params: FilterDto) => {
    const response = await axiosInstance.get(`payout/customer/balance/history`, {
        params: {
            page: params.page || 1,
            limit: params.limit || 10,
        }
    });
    return response.data;
}

// Admin manual actions on a payout transaction
const confirmWacepayTransaction = async (transactionId: string) => {
    const response = await axiosInstance.post(`payout/admin/wacepay/transactions/${transactionId}/confirm`);
    return response.data;
};

const forceSuccessTransaction = async (transactionId: string, reason?: string) => {
    const response = await axiosInstance.post(`payout/admin/transactions/${transactionId}/force-success`, { reason });
    return response.data;
};

const forceFailTransaction = async (transactionId: string, reason?: string) => {
    const response = await axiosInstance.post(`payout/admin/transactions/${transactionId}/force-fail`, { reason });
    return response.data;
};

// Uses transCode (e.g. POMQPN7J7AEFDJWN), not the UUID id
const reconcileTransaction = async (transCode: string) => {
    const response = await axiosInstance.post(`payout/admin/transactions/${transCode}/reconcile`);
    return response.data;
};

export const payoutService = {
    getAllAccounts,
    activateAccount,
    deactivateAccount,
    subscribeService,
    updateService,
    deactivateService,
    getOneService,
    getServicesForCustomer,
    getAllTransactions,
    activateAccountCustomer,
    getAccount,
    initiatePayout,
    fundAccount,
    getFundings,
    getAllFundingsAdmin,
    approveFundingAdmin,
    rejectFundingAdmin,
    processFundingAdmin,
    completeFundingAdmin,
    cancelFundingAdmin,
    getTransactionCustomer,
    getOneTransaction,
    getCustomerStats,
    getCustomerStatsByCurrency,
    getMyServices,
    getBalanceHistory,
    confirmWacepayTransaction,
    forceSuccessTransaction,
    forceFailTransaction,
    reconcileTransaction,
};
